import httpClient from '../plugins/axios'
import homeAdditionalCovers from '@/assets/maps/homeAdditionalCovers.json'
import { getStorage } from '@/utils/localstorage'
import getPolicyProductType from '@/shared/policy/getPolicyProductType'
import i18n from '@/i18n'
import { customerTypeMap } from '@/constants/prodCodes'
import { INITIAL_TRANS_ID_STORE_NAME } from '@/constants/constants.ts'

const defaultState = {
  productType: null,
  policyInfo: null,
  renewScheme: null,
  policySchemesInfo: {
    policyNumber: null,
    promocode: null,
    info: {
      quoteno: null,
      schemes: null
    }
  },
  quoteNumber: null,
  filter: null,
  promocode: null,
  byUrl: false,
  userData: {
    qid: null,
    policy: null,
    phone: null,
    makeCode: null,
    modelCode: null,
    email: null,
    transId: null,
    plateNumber: null
  },
  phoneNumberInfo: {
    countryCode: null,
    phone: null,
    formattedPhone: null
  },
  homeCurrentAdditionalCovers: {
    schemeCode: null,
    quoteNumber: null,
    covers: []
  }
}

const lsEntries = {
  motor: 'car-insurance_renew-login',
  home_boat: 'home-boat-insurance_renew-login'
}

function getLoginState (product = 'motor') {
  const lsKey = lsEntries[product]
  return lsKey && getStorage(lsKey)
}

function saveState (state) {
  localStorage.setItem('qic_renew', JSON.stringify(state))
}

function saveToInitialState (toSaveInPolicy, toSave = {}, product = 'motor') {
  const ls = getLoginState(product)
  const lsKey = lsEntries[product]

  localStorage.setItem(lsKey, JSON.stringify({
    ...ls,
    ...toSave,
    policy: {
      ...ls.policy,
      ...toSaveInPolicy
    }
  }))
}

function savedState () {
  let result = {}

  try {
    result = JSON.parse(localStorage.getItem('qic_renew'))
  } catch { }

  return result
}

export default {
  state () {
    return {
      productKey: 'motor',
      ...defaultState,
      ...savedState()
    }
  },
  getters: {
    getUserPhoneInfo (state) {
      return state.phoneNumberInfo
    },
    productInfoName (state) {
      if (state.productType === 'moto') {
        return i18n.t('renew.quote_page.header.motorcycle_info')
      }
      return i18n.t('car_info_header')
    }
  },
  mutations: {
    setProductKey (state, productKey) {
      state.productKey = productKey
    },
    setProductType (state, productType) {
      state.productType = productType
      saveState(state)
    },
    setPolicyInfo (state, policyInfo) {
      state.policyInfo = policyInfo
      saveState(state)
    },
    setRenewScheme (state, renewScheme) {
      state.renewScheme = renewScheme
      saveState(state)
    },
    setQuoteNumber (state, quoteNumber) {
      state.quoteNumber = quoteNumber
      saveState(state)
    },
    setRenewFilter (state, filter) {
      state.filter = filter
      saveState(state)
    },
    setUserPolicy (state, policy) {
      state.userData.policy = policy
      saveState(state)
    },
    setUserPhone (state, phone) {
      state.userData.phone = phone
      saveState(state)
      saveToInitialState({ mobileNo: phone }, { phone }, state.productKey)
    },
    setUserEmail (state, email) {
      state.userData.email = email
      saveState(state)
      saveToInitialState({ emailId: email }, {}, state.productKey)
    },
    setUserQid (state, qid) {
      state.userData.qid = qid
      state.policyInfo.civilId = qid
      saveState(state)
      saveToInitialState({ civilId: qid }, { qid }, state.productKey)
    },
    setFullName (state, insName) {
      state.policyInfo.insName = insName

      saveState(state)
      saveToInitialState({ insName }, {}, state.productKey)
    },
    setUserTransId (state, transId) {
      state.userData.transId = transId
      saveState(state)
    },
    setMakeCode (state, makeCode) {
      state.userData.makeCode = makeCode
      saveState(state)
    },
    setModelCode (state, modelCode) {
      state.userData.modelCode = modelCode
      saveState(state)
    },
    setPlateNumber (state, regNo) {
      state.policyInfo.regNo = regNo
      saveState(state)
      saveToInitialState({ regNo }, {}, state.productKey)
    },
    setVehicleShape (state, vehicleShape) {
      state.policyInfo.vehicleShape = vehicleShape
      saveState(state)
      saveToInitialState({ vehicleShape }, {}, state.productKey)
    },
    setPlateCharacter (state, plateCharacter) {
      state.policyInfo.plateCharacter = plateCharacter
      saveState(state)
      saveToInitialState({ plateCharacter }, {}, state.productKey)
    },
    setPromocode (state, promocode) {
      state.promocode = promocode
      saveState(state)
    },
    clearUserData (state) {
      state.userData.qid = null
      state.userData.policy = null
      state.userData.phone = null
      state.phoneNumberInfo = {
        countryCode: null,
        phone: null,
        formattedPhone: null
      }
      state.policyInfo = null
      state.renewScheme = null
      state.policySchemesInfo = {
        policyNumber: null,
        promocode: null,
        info: {}
      }
      saveState(state)
    },
    setCurrentAdditionalCovers (state, covers) {
      state.homeCurrentAdditionalCovers.covers = covers
      saveState(state)
    },
    setCurrentAdditionalCoversQuote (state, quoteNumber) {
      state.homeCurrentAdditionalCovers.quoteNumber = quoteNumber
      saveState(state)
    },
    setUserPhoneInfo (state, contacts) {
      state.phoneNumberInfo.phone = contacts?.phone
      state.phoneNumberInfo.countryCode = contacts?.countryCode
      state.phoneNumberInfo.formattedPhone = contacts?.formattedPhone
      saveState(state)
    },
    setPolicySchemesInfo (state, { info, policyNumber, promocode }) {
      state.policySchemesInfo.info = info
      state.policySchemesInfo.policyNumber = policyNumber
      state.policySchemesInfo.promocode = promocode
      saveState(state)
    },
    setByUrl (state, byUrl) {
      state.byUrl = byUrl
    }
  },
  actions: {
    getTotalRenewPrice (_, { data, method }) {
      const formData = Object.entries(data).reduce((d, [key, value]) => {
        if (Array.isArray(value)) {
          return value.reduce((prev, item) => {
            return `${prev ? prev + '&' : ''}${key}[]=${item}`
          }, d)
        }
        return `${d ? d + '&' : ''}${key}=${value}`
      }, '')
      return httpClient.post(method, formData)
        .then(({ data }) => ({
          totalPrice: Number(data.totalprice),
          perMonth: data?.permonth ? parseFloat(data.permonth) : null
        }))
    },
    getRenewSchemes ({ state, dispatch }) {
      const productCode = state.policyInfo?.prodCode
      return dispatch('getRenewPolicy')
        .then((policy) => {
          const schemes = policy.schemes
          if (schemes.length === 1) {
            return schemes
          }
          return schemes
            .filter(s => s.productcode === productCode)
            .map(s => {
              s.fullPrice = Number(s?.price) + Number(s?.discount)
              return s
            }) || []
        })
    },
    getRenewQuote ({ state, dispatch, commit }) {
      const schemeCode = state.renewScheme.schemecode
      const type = state.productType
      return dispatch('getRenewPolicy')
        .then((policy) => {
          commit('setQuoteNumber', policy.quoteno)
          const quote = {
            quoteNumber: policy.quoteno,
            scheme: policy.schemes.find(s => s.schemecode === schemeCode)
          }
          if (type === 'home') {
            const coversPrices = homeAdditionalCovers
            const covers = []
            for (const id of quote.scheme.ids) {
              if (!coversPrices[id.id]) continue
              id.cover = coversPrices[id.id].values[0]
              covers.push(id)
            }
            quote.scheme.ids = covers
            commit('setCurrentAdditionalCovers', quote.scheme.ids)
            commit('setCurrentAdditionalCoversQuote', policy.quoteno)
          }
          return quote
        })
    },
    setUserLoginData ({ commit }, product = 'motor') {
      const ls = getLoginState(product)
      if (!ls) return

      if (product !== 'motor') {
        commit('setProductKey', product)
      }

      const { policy, phone, policyNo, qid } = ls
      if (!policy) return
      const policyModified = {
        instYN: policy.inst,
        renwAllowYN: policy.renwAllow,
        prntReceiptYN: policy.prntReceipt,
        regClaimYN: policy.regClaim,
        activeYn: policy.active,
        paymentLinkYN: policy.paymentLink,
        ...policy
      }

      const initialTransId = getStorage(INITIAL_TRANS_ID_STORE_NAME)
      let transId = initialTransId

      if (process.env.VUE_APP_IS_DEV) {
        commit('setPolicyInfo', policy)
        commit('setUserPhone', policyModified.mobileNo)
        commit('setUserPolicy', policyModified.policyNo)
        commit('setUserQid', policyModified.civilId)
        commit('setProductType', getPolicyProductType(policyModified))

        if (!transId) {
          transId = policyModified.civilId.slice(qid.length - 4)
        }

        commit('setUserTransId', initialTransId)
      } else {
        commit('setPolicyInfo', policyModified)
        commit('setUserPhone', phone)
        commit('setUserPolicy', policyNo)
        commit('setUserQid', qid)
        commit('setProductType', getPolicyProductType(policyModified))

        if (!transId) {
          transId = qid.slice(qid.length - 4)
        }
      }

      if (!initialTransId) {
        localStorage.setItem(INITIAL_TRANS_ID_STORE_NAME, JSON.stringify(transId))
      }

      commit('setUserTransId', transId)
    },
    getRenewPolicy ({ state, commit }, forceUpdate) {
      const policyInfo = state.policyInfo
      const policySchemesInfo = state.policySchemesInfo
      const type = state.productType
      const promocode = state.promocode
      if (!policyInfo) throw new Error('Choose policy first')
      if (!forceUpdate && policyInfo?.policyNo === policySchemesInfo.policyNumber && promocode === policySchemesInfo.promocode) {
        return policySchemesInfo.info
      }
      const params = {
        policyNo: policyInfo?.policyNo
      }

      if (promocode) params.promocode = promocode

      let policyApiEndPoint = ''
      if (type === 'boat') {
        policyApiEndPoint = '/policyrenewalboats'
      } else if (type === 'home') {
        policyApiEndPoint = '/policyrenewalhome'
      } else {
        policyApiEndPoint = '/policyrenewal'
      }

      return httpClient.get(policyApiEndPoint, {
        params,
        headers: { 'Qic-Product-Code': 'qarenew' }
      })
        .then(response => {
          commit('setPolicySchemesInfo', {
            policyNumber: policyInfo?.policyNo,
            promocode,
            info: { quoteno: response.data.quoteno, schemes: response.data.schemes }
          })
          return response.data
        })
    },
    redirectToRenewPayment ({ dispatch }, { paymentMethod, paymentPlan, ids }) {
      return dispatch('getRenewPrice', {
        ids,
        selectproduct: true,
        paymentmethod: paymentMethod
      })
        .then(() => {
          let link = paymentMethod === 0 ? process.env.VUE_APP_REDIRECT_CC : process.env.VUE_APP_REDIRECT_DC
          if (paymentPlan === 'monthly') {
            link = process.env.VUE_APP_REDIRECT_CC_MONTHLY + '?action=installment'
          }
          window.location.href = link
        })
    },
    getRenewPrice ({ state, dispatch }, data) {
      const type = state.productType
      data.productcode = state.renewScheme.productcode
      data.schemecode = state.renewScheme.schemecode
      data.quoteno = state.quoteNumber
      if (type === 'car' || type === 'moto') {
        if (state.changePlan.status && state.changePlan.prodCode === '0110' && state.motorUpgradeSchemes.info.schemes) {
          data.quoteno = state.motorUpgradeSchemes.info.quoteno
        }
        return dispatch('getTotalRenewPrice', { data, method: 'gettotalprice' })
      }
      if (type === 'home') {
        return dispatch('getTotalRenewPrice', { data, method: 'gethomeprice' })
      }
      if (type === 'boat') {
        return dispatch('getTotalRenewPrice', { data, method: 'getboatsprice' })
      }
      throw new Error('')
    },
    changeAdditionalCoverPrice ({ dispatch, state, commit }, { value, coverCode }) {
      return dispatch('setAdditionalCoverPrice', {
        quoteno: state.quoteNumber,
        prodcode: '0140',
        covercode: coverCode,
        value
      })
        .then(({ covers: schemesPrices }) => {
          const currentAdditionalCovers = state.homeCurrentAdditionalCovers.covers
          const schemeCode = state.renewScheme?.schemecode
          const newPrice = schemesPrices.find(s => s.schCode === schemeCode).premium
          currentAdditionalCovers.filter(cover => cover.id === coverCode).forEach(cover => {
            cover.value = newPrice
            cover.cover = value
          })
          commit('setCurrentAdditionalCovers', currentAdditionalCovers)
          return currentAdditionalCovers.find(c => c.id === coverCode)
        })
    },
    setUserPhoneInfo ({ commit }, contacts) {
      commit('setUserPhoneInfo', contacts)
    },
    isPromocdoeValid (_, { promocode, type }) {
      this.dispatch('setIsSuccessPromocode', false)
      return this.dispatch('validatePromocode', promocode)
        .then((data) => {
          const products = ['TP', 'OD']
          const usage = data?.codeUsage.find(usage =>
            usage.transactionType === 'REN' && products.includes(usage.productType)
          )
          if (usage && new Date(usage.expirationDate) >= new Date()) return true
          return false
        })
        .catch(() => {
          return false
        })
    },
    getQatarWordings (_, params) {
      return httpClient.get('/getqatarwordings', { params })
    },
    sendFormDataWithInfo ({ state, dispatch, commit }) {
      const nameData = {}

      if (state.policyInfo.insName) {
        const nameList = state.policyInfo.insName.split(' ').filter(n => !!n)
        nameData.first_name = nameList[0]
        nameData.last_name = nameList.slice(1).join(' ')
      }

      return dispatch('sendFormData', {
        ...nameData,
        phone: state.userData.phone,
        email: state.userData.email,
        qatar_id: state.userData.qid,
        customer_type: state.userData.qid.length === 8 ? customerTypeMap.COMPANY : customerTypeMap.INDIVIDUAL,
        vehicle_type_renew: state.policyInfo.vehicleShape,
        plate_type: state.policyInfo.plateCharacter,
        plate: state.policyInfo.regNo
      })
    }
  }
}
